import React, { useEffect, useContext, useState, useCallback } from 'react';
import theme from 'theme';
import i18n from 'i18n-js';
import { round } from 'utils';
import { debounce } from 'lodash';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { Table } from 'components/UI';
import { useStyles } from './Snapshots.styled';
import InfoIcon from '@material-ui/icons/Info';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardActions } from 'state/modules/dashboard';
import { Grid, Box, Typography, Paper, Tooltip } from '@material-ui/core';

const Statistics = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { orgType } = useContext(UserContext);
  const config = useSelector((state) => state.config?.settings);
  const { aggregatesTotals } = useSelector((state) => state.dashboard);
  const dashboardSettings = config.integrations?.dashboard_settings?.noc;
  const {
    fleetAggregates: { list, pagination },
    fetchingFleetAggregates,
  } = useSelector((state) => state.dashboard);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    sort_by_name: true,
    upcoming: dashboardSettings?.upcoming || 6,
    type: orgType === 'msOwnerOrg' ? 'location' : 'service',
  });

  const columns = [
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            className={classes.headerLabel}
          >
            {i18n.t('common.owner')}
          </Box>
          <Box alignItems="start" style={{ height: 56 }} className={classes.columnHeaderInline}>
            <Typography className={classes.inlineLabel}>
              <Box component="span" className={classes.headerLabelSingle} lineHeight={1}>
                {i18n.t('dashboard.statistics.service_area')}
              </Box>
            </Typography>
            <Tooltip
              title={i18n.t('dashboard.statistics.service_area_description')}
              placement="right"
            >
              <InfoIcon fontSize="small" className={classes.helpIconInline} />
            </Tooltip>
          </Box>
        </Box>
      ),
      selector: 'name',
      cell: (row) => (orgType === 'msOwnerOrg' ? row?.name || '--' : row?.parent?.name || '--'),
      grow: 1,
      omit: orgType === 'plainOrg',
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            className={classes.headerLabel}
          >
            {i18n.t('common.service')}
          </Box>
          <Box className={classes.columnHeaderInline} style={{ height: 56 }} alignItems="start">
            <Typography className={classes.inlineLabel}>
              <Box component="span" className={classes.headerLabelSingle}>
                {i18n.t('dashboard.statistics.vehicle_group')}
              </Box>
            </Typography>
            <Tooltip
              title={i18n.t('dashboard.statistics.vehicle_group_description')}
              placement="right"
            >
              <InfoIcon fontSize="small" className={classes.helpIconInline} />
            </Tooltip>
          </Box>
        </Box>
      ),
      selector: 'name',
      cell: (row) => row?.group?.name || row.name || '--',
      grow: 2,
      omit: orgType === 'msOwnerOrg',
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('dashboard.statistics.available')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.for_customers')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.available_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.available || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '110px',
      maxWidth: '110px',
      cell: (row) => row?.available || row?.totals?.available || 0,
      selector: 'available',
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.customer')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.customer_in_progress')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.customer_in_progress_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.inprogress_customer || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '91px',
      maxWidth: '91px',
      cell: (row) => row.inprogress_customer || row?.totals?.inprogress_customer || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.customer')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.customer_late_check_in')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.customer_late_check_in_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.reserved_customer || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '100px',
      maxWidth: '105px',
      cell: (row) => row?.reserved_customer || row?.totals?.reserved_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.customer')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.customer_late_return')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.customer_late_return_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.late_return_customer || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '95px',
      maxWidth: '95px',
      cell: (row) => row?.late_return_customer || row?.totals?.late_return_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.customer')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.customer_upcoming')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.customer_upcoming_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.upcoming_customer || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '65px',
      maxWidth: '75px',
      cell: (row) => row.upcoming_customer || row?.totals?.upcoming_customer || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.employee')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.employee_in_use')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.employee_in_use_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.in_use_employee || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '55px',
      maxWidth: '70px',
      cell: (row) => row.in_use_employee || row?.totals?.in_use_employee || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.employee')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.employee_late_return')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.employee_late_return_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.late_return_employee || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '95px',
      maxWidth: '95px',
      cell: (row) => row.late_return_employee || row?.totals?.late_return_employee || 0,
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.other')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.other_orgs_in_use')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.other_orgs_in_use_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.in_use_other || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '75px',
      maxWidth: '75px',
      cell: (row) => row.in_use_other || row?.totals?.in_use_other || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: (
        <Box display="flex" flexDirection="column">
          <Box
            height={30}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.headerLabel}
          >
            {i18n.t('common.other')}
          </Box>
          <Box className={classes.columnHeader} alignItems="center">
            <Box className={classes.columnHeaderInline}>
              <Typography className={classes.inlineLabel}>
                <Box component="span" className={classes.headerLabelSingle}>
                  {i18n.t('dashboard.statistics.other_orgs_late_return')}
                </Box>
              </Typography>
              <Tooltip
                title={i18n.t('dashboard.statistics.other_orgs_late_return_description')}
                placement="right"
              >
                <InfoIcon fontSize="small" className={classes.helpIconInline} />
              </Tooltip>
            </Box>
            <Typography>
              <Box component="span" className={classes.headerSubLabel}>
                {aggregatesTotals?.late_return_other || 0}
              </Box>
            </Typography>
          </Box>
        </Box>
      ),
      center: true,
      grow: 1,
      minWidth: '95px',
      maxWidth: '95px',
      cell: (row) => row?.late_return_other || row?.totals?.return_other || 0,
      style: {
        justifyContent: 'center',
      },
    },
  ];

  const getFleetAggregates = (params) => {
    dispatch(DashboardActions.fetchAggregates(params));
  };

  const getTotals = (params) => {
    dispatch(DashboardActions.fetchAggregatesTotals(params));
  };

  const updateData = useCallback((params) => {
    getFleetAggregates(params);
    getTotals(params);
  }, []);

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    setParams({ ...params, ...newParams });
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    setParams({ ...params, ...newParams });
  };

  const handleChangeFilter = debounce((filters) => {
    setParams({ ...filters });
  }, 500);

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && key !== 'keyword' && delete filters[key]
    );
    if (filters?.keyword) {
      filters.keyword = truncateKeywordSpaces(filters.keyword);
    }
    handleChangeFilter({ ...filters });
  };

  useEffect(() => {
    updateData(params);
  }, [params]); // eslint-disable-line

  const tableExtraStyles = {
    headCells: {
      style: {
        padding: '0 0.25rem',
      },
    },
    cells: {
      style: {
        padding: '0 0.25rem',
      },
    },
    rows: {
      style: {
        minHeight: 44,
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        borderRight: `1px solid ${theme.palette.tables.border}`,
        borderBottom: `1px solid ${theme.palette.tables.border}`,
      },
    },
  };

  return (
    <Card
      enableCollapse
      id="snapshots-widget"
      title="Usage Snapshot"
      headerActionType="vertical"
      onRefresh={() => {
        updateData(params);
      }}
      loading={fetchingFleetAggregates}
    >
      <Box>
        <Grid container xs={12}>
          <Grid item xs={12}>
            <Box mb={4}>
              <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-end"
              >
                <Grid item xs={12} sm={4} md={3}>
                  <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
                    <Grid item>
                      <SearchIcon className={classes.searchIcon} />
                    </Grid>
                    <Grid item style={{ width: '100%' }}>
                      <SearchField
                        allowClear
                        margin="none"
                        label={
                          orgType === 'msOwnerOrg'
                            ? i18n.t('dashboard.search_service_area')
                            : i18n.t('dashboard.search_vehicle_group')
                        }
                        field={{
                          onChange: search,
                          name: 'keyword',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={list}
                columns={columns}
                loading={fetchingFleetAggregates}
                handlePageChange={handlePageChange}
                page={params.page}
                limit={params.limit}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                extraStyles={tableExtraStyles}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Statistics;
